import type { ReactNode } from "react";
import { createUseStyles } from "react-jss";
import type { Boat } from "../../../../common/src";
import { boatPhotoUrl } from "../../../../common/src";
import type { VetBoatsTheme } from "../../theme";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    background: theme.color.surface,
    border: `1px solid ${theme.color.border}`,
    borderRadius: theme.radius,
    boxShadow: theme.shadow,
    overflow: "hidden",
    minWidth: 0,
  },
  muted: { opacity: 0.8 },
  media: {
    display: "block",
    aspectRatio: "3 / 2",
    // Without clipping, a tall photo would stretch the box past 3:2.
    overflow: "hidden",
    background: `linear-gradient(160deg, #e8f1f7, #d3e4ef)`,
  },
  photo: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  placeholder: {
    display: "block",
    width: "62%",
    height: "100%",
    margin: "0 auto",
    objectFit: "contain",
    opacity: 0.55,
  },
  body: {
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: 1,
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    alignItems: "baseline",
  },
  title: {
    margin: 0,
    fontSize: 18,
    "& a": { color: theme.color.text, textDecoration: "none" },
    "& a:hover": { textDecoration: "underline" },
  },
  facts: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 12px",
    color: theme.color.muted,
    fontSize: 14,
  },
  footer: { marginTop: "auto", paddingTop: 6 },
}));

interface BoatCardProps {
  boat: Boat;
  /** Where the photo and title link to. */
  href: string;
  /** Shown beside the title, e.g. a status badge. */
  badge?: ReactNode;
  /** Extra content: availability reasons, booking panel, actions. */
  children?: ReactNode;
  dimmed?: boolean;
}

const days = (n: number): string => `${n} day${n === 1 ? "" : "s"}`;

export const BoatCard = ({
  boat,
  href,
  badge,
  children,
  dimmed = false,
}: BoatCardProps) => {
  const classes = useStyles();
  return (
    <article className={`${classes.card} ${dimmed ? classes.muted : ""}`}>
      <a className={classes.media} href={href} tabIndex={-1} aria-hidden>
        {boat.photoUpdatedAt ? (
          <img
            className={classes.photo}
            src={boatPhotoUrl(boat.id, boat.photoUpdatedAt)}
            alt=""
            loading="lazy"
          />
        ) : (
          <img className={classes.placeholder} src="/vb_logo.svg" alt="" />
        )}
      </a>
      <div className={classes.body}>
        <div className={classes.titleRow}>
          <h3 className={classes.title}>
            <a href={href}>{boat.name}</a>
          </h3>
          {badge}
        </div>
        <div className={classes.facts}>
          {boat.makeModel && (
            <span>
              {boat.makeModel}
              {boat.lengthFeet ? `, ${boat.lengthFeet} ft` : ""}
            </span>
          )}
          <span>Seats {boat.capacity}</span>
          <span>Up to {days(boat.maxConsecutiveDays)}</span>
          {boat.storageLocation && <span>{boat.storageLocation}</span>}
          <span>
            {boat.damagePolicy === "ownerWaives"
              ? "Owner covers boat damage"
              : "Borrower pays boat damage"}
          </span>
        </div>
        {children && <div className={classes.footer}>{children}</div>}
      </div>
    </article>
  );
};

/** Responsive grid of boat cards. */
export const useBoatGridStyles = createUseStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: 18,
    alignItems: "stretch",
  },
});
